import { Sparkle as SparkleIcon, Unarchive } from "@/components/icons/customIcons.tsx";
import { type FocusType, Omnibox } from "@/components/messages/Omnibox/Omnibox.tsx";
import { OmniArtifactFormBiscuit } from "@/components/artifacts/OmniArtifactFormBiscuit.tsx";
import { OmniArtifactFormCard } from "@/components/artifacts/OmniArtifactFormCard.tsx";
import {
  ArtifactBiscuit,
  OMNI_ROW_BAND_CLASS,
  OMNI_ROW_RULE_CLASS,
} from "@/components/artifacts/ArtifactBiscuit.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { cn, isCoarsePointer } from "@/lib/utils.ts";
import { formAwaitsAnswer } from "@/types/artifacts.ts";
import { hasOmniCardContent, type OmniArtifact } from "@/shared/selectOmniArtifacts.ts";
import { useScrollToBottom } from "@/hooks/useScrollToBottom.tsx";
import { ArrowDownIcon } from "lucide-react";
import type { GetThreadResp } from "@/types/thread.ts";
import { UNITS_EXHAUSTED_REASON } from "@/types/entitlements.ts";
import { settingsTabPath } from "@/views/Settings/settingsNav.ts";
import { useState, type RefObject } from "react";
import { Link } from "wouter";
import { ComputerDesktopOverlay } from "@/components/artifacts/omni/ComputerDesktopOmniBody.tsx";
import { ExternalChannelComposerNotice } from "./ExternalChannelComposerNotice.tsx";
import { BrowserTakeoverBar } from "./browserTakeover/BrowserTakeoverBar.tsx";
import type { SendError, SendMessageFn } from "./hooks/useMessageList.tsx";
import { QueuedMessageStack } from "./QueuedMessageStack.tsx";
import type { useOmniReview } from "./useOmniReview.ts";

/**
 * Floating action button at the bottom of the chat.
 * Handles scroll to bottom and notifications about new content out of view
 */
const ScrollToBottomButton = ({
  isAtBottom,
  scrollContainerRef,
  className,
}: {
  isAtBottom: boolean;
  scrollContainerRef: RefObject<HTMLElement | null>;
  className?: string;
}) => {
  const scrollToBottom = useScrollToBottom(scrollContainerRef);

  return (
    <div
      className={cn(
        "ease-out transition-[opacity,visibility]",
        isAtBottom ? "opacity-0 invisible" : "",
        className,
      )}
    >
      <Button
        onClick={() => scrollToBottom("smooth")}
        variant="secondary"
        size="icon"
        className="border-color-border bg-background"
        data-statsig-name="thread-scroll-to-bottom"
      >
        <ArrowDownIcon className="size-4" />
      </Button>
    </div>
  );
};

/**
 * Everything sticky at the bottom of a thread: the scroll-to-bottom button, the
 * queued-message stack, the external-channel notice, and the composer itself —
 * which is an Omnibox, a card review, or an Unarchive button depending on what
 * the thread is waiting on.
 *
 * The review arrives as one object rather than nineteen props. Its shape is the
 * hook's business, and threading each field through the call site made the
 * boundary look far wider than it is.
 */
export const ThreadComposerFooter = ({
  thread,
  review,
  readOnly,
  isAtBottom,
  scrollContainerRef,
  queuedUserItems,
  removeSessionEvent,
  showExternalThreads,
  isWebView,
  onSendMessage,
  isSendingMessage,
  isMessagesPending,
  onEscapeKeyPressed,
  focusType,
  prefillOmniboxRef,
  sendError,
  onUnarchive,
}: {
  thread: GetThreadResp;
  review: ReturnType<typeof useOmniReview>;
  readOnly: boolean;
  isAtBottom: boolean;
  scrollContainerRef: RefObject<HTMLElement | null>;
  queuedUserItems: Parameters<typeof QueuedMessageStack>[0]["queuedItems"];
  removeSessionEvent: (id: number) => void;
  showExternalThreads: boolean;
  isWebView: boolean;
  onSendMessage: SendMessageFn;
  isSendingMessage: boolean;
  isMessagesPending: boolean;
  onEscapeKeyPressed?: () => boolean;
  focusType: FocusType;
  prefillOmniboxRef: RefObject<((text: string) => void) | null>;
  sendError: SendError | null;
  onUnarchive: () => void;
}) => {
  const [browserControlContainer, setBrowserControlContainer] = useState<HTMLDivElement | null>(
    null,
  );
  const {
    biscuitArtifact,
    isQueue,
    activeOmniIndex,
    cards,
    decidedCount,
    commitDecisions,
    stepOmniArtifact,
    advanceOrCommit,
    stageActionAndAdvance,
    skipRemaining,
    stageNote,
    stageActiveFormValues,
    stageFormAndAdvance,
    handleNoteKeyDown,
    dismissOverlay,
    activeNoteText,
    activeFormValues,
    activeFormFiles,
    setActiveFormFiles,
    decisions,
  } = review;

  /**
   * Whether the card about to be handed the surface has something to draw.
   *
   * This is the cheap check, and deliberately not the safety net. It reads the
   * review snapshot, while the card resolves its own artifact from the artifact
   * cache — which by design outranks that snapshot (see `useArtifact`). An
   * artifact settling under an open card reaches the cache first, so this can
   * say yes to a card that then draws nothing.
   *
   * What keeps the composer alive is the `fallback` handed to the card below,
   * decided where the artifact is actually read (BIG-7439). This only keeps an
   * empty wrapper off screen in the common case, where the snapshot is right.
   *
   * The `biscuitArtifact &&` in the branch below is redundant with this and
   * kept for the narrowing, which the card's own props need.
   */
  const hasBiscuit = !!biscuitArtifact && hasOmniCardContent(biscuitArtifact);

  /**
   * Whether the card holding the surface puts choices above the composer.
   *
   * A card like that is asking the user to choose something, and on a phone
   * taking focus answers it with a keyboard drawn over the very rows being
   * offered. Action rows are the whole test: a picker carries a "Done" row of
   * its own, so it counts here without a separate look at its body. A form is
   * the other case and deliberately excluded — its input is the question, not an
   * extra beneath one.
   */
  const cardOffersChoices =
    hasBiscuit &&
    biscuitArtifact.artifactType !== "userResponse" &&
    (biscuitArtifact.actions?.length ?? 0) > 0;

  /**
   * The focus policy the follow-up row gets: the thread's own, unless a card is
   * asking a question of a phone.
   *
   * Resolved against the pointer here rather than handed down as "desktop-only",
   * so the value a desktop sees never moves. The composer's focus effect keys on
   * this prop and reads any change as an instruction to take focus, so a value
   * that shifted as cards arrived and settled would pull the caret back mid-turn
   * from wherever the user had put it.
   */
  const followUpFocusType: FocusType = cardOffersChoices && isCoarsePointer() ? "never" : focusType;

  // The last row of whichever biscuit is on screen. In a queue it's the
  // per-artifact note input (controlled by the staged decision, so the text
  // follows the artifact); otherwise it's the regular Omnibox, restyled to read
  // as a peer row rather than as its own card — the only place to type while the
  // biscuit owns the omnibox area, so every biscuit gets it.
  const omniFollowUp = isQueue ? (
    <div
      className={cn(
        "flex flex-row items-center gap-2 pl-2.5 pr-4",
        OMNI_ROW_BAND_CLASS,
        OMNI_ROW_RULE_CLASS,
      )}
    >
      <span className="size-8 shrink-0 flex items-center justify-center rounded-sm bg-accent text-accent-foreground">
        <SparkleIcon className="size-4" />
      </span>
      <Input
        type="text"
        // Focus the note box as soon as the queue appears so the user can type /
        // Tab-navigate immediately. Never on a phone, where the pager and the
        // decisions it steps through sit above this row and a keyboard would
        // cover them; and never against a caller that asked for no focus at all,
        // which the row would otherwise be the one place to disobey.
        autoFocus={followUpFocusType !== "never" && !isCoarsePointer()}
        value={activeNoteText}
        onChange={(e) => biscuitArtifact && stageNote(biscuitArtifact.id, e.currentTarget.value)}
        onKeyDown={handleNoteKeyDown}
        disabled={commitDecisions.isPending}
        placeholder="Add a note"
        enterKeyHint="send"
        // Flush, borderless row to match the omnibox follow-up look.
        className="flex-1 min-w-0 h-auto rounded-none border-0 bg-transparent px-0 py-0 shadow-none text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
      />
    </div>
  ) : (
    <Omnibox
      thread={thread}
      onSubmit={onSendMessage}
      isSendingMessage={isSendingMessage}
      disabled={isMessagesPending || isSendingMessage}
      onEscapeKeyPressed={onEscapeKeyPressed}
      focusType={followUpFocusType}
      className={cn(
        "border-0! shadow-none! rounded-none! w-full mx-0! bg-transparent! pl-2.5! pr-4",
        "flex! flex-row! items-center! gap-2!",
        OMNI_ROW_BAND_CLASS,
        OMNI_ROW_RULE_CLASS,
      )}
      placeholder="Ask a follow up"
      prefillRef={prefillOmniboxRef}
      hideInputControls
      enterKeyHint="send"
      textareaLeadingElement={
        <span className="size-8 shrink-0 flex items-center justify-center rounded-sm bg-accent text-accent-foreground">
          <SparkleIcon className="size-4" />
        </span>
      }
    />
  );

  // The composer on its own, as it looks when no card has the surface. Also
  // what a card falls back to when it turns out to have nothing to draw, so the
  // thread is never left without somewhere to type.
  const standaloneOmnibox = (
    <Omnibox
      thread={thread}
      onSubmit={onSendMessage}
      isSendingMessage={isSendingMessage}
      disabled={isMessagesPending || isSendingMessage}
      onEscapeKeyPressed={onEscapeKeyPressed}
      focusType={focusType}
      // No shadow here: the pill inside carries its own, and one set on the
      // form draws a square corner behind the pill's round end.
      className="w-[688px]"
      prefillRef={prefillOmniboxRef}
    />
  );

  /**
   * The card an artifact gets when it holds the composer area.
   *
   * Guard clauses rather than one conditional expression: a form awaiting an
   * answer, an answered form and a heavy artifact are three different things to
   * show, not three variations on one calculation.
   *
   * Answered-ness is read off the form rather than through `isUnansweredForm`:
   * that predicate narrows to `UserResponseArtifact`, so TypeScript takes its
   * false branch to mean "not a form at all", which an answered one still is.
   * `formAwaitsAnswer` narrows the FORM, so it composes here, and it also counts
   * a form answered by a prose reply as answered, where reading `values` alone
   * left that one fully answerable.
   */
  const renderArtifactCard = (artifact: OmniArtifact) => {
    if (artifact.artifactType !== "userResponse") {
      return (
        <ArtifactBiscuit
          artifact={artifact}
          threadId={thread.id}
          browserControlRef={setBrowserControlContainer}
          onDismiss={dismissOverlay}
          pagination={
            isQueue
              ? {
                  index: activeOmniIndex,
                  total: cards.length,
                  onPrev: () => stepOmniArtifact(-1),
                  onNext: advanceOrCommit,
                }
              : undefined
          }
          staging={
            isQueue
              ? {
                  decision: decisions.get(artifact.id),
                  decidedCount,
                  isSubmitting: commitDecisions.isPending,
                  onStageAction: (action) => stageActionAndAdvance(artifact.id, action),
                  onSkip: skipRemaining,
                }
              : undefined
          }
          followUp={omniFollowUp}
          fallback={standaloneOmnibox}
        />
      );
    }

    // A form is something to fill in, so it answers in place as a stepped card —
    // one question at a time, ←/→ between them — staging its answers into the
    // same one-run batch as the heavy-artifact decisions. Its expand control
    // opens every question at once on the canvas (BIG-7271).
    //
    // The card is the composer while it holds the surface, so its answer box
    // takes files the way the composer does and they commit with the answers
    // (BIG-9261).
    if (formAwaitsAnswer(artifact.form)) {
      return (
        <OmniArtifactFormCard
          // Re-init field index + answers when the active form changes (a queue
          // can hold more than one form).
          key={artifact.id}
          artifact={artifact}
          threadId={thread.id}
          stagedValues={activeFormValues}
          isSubmitting={commitDecisions.isPending}
          pagination={isQueue ? { index: activeOmniIndex, total: cards.length } : undefined}
          decidedCount={isQueue ? decidedCount : undefined}
          onStageValues={stageActiveFormValues}
          onPrevArtifact={() => stepOmniArtifact(-1)}
          onAdvance={stageFormAndAdvance}
          onDismiss={dismissOverlay}
          attachedFiles={activeFormFiles}
          onFilesChange={activeFormFiles ? setActiveFormFiles : undefined}
        />
      );
    }

    // Answered: nothing left to step through, and the review holds a decided
    // card so the pager can reach it. The biscuit shows the answers that went in.
    return (
      <OmniArtifactFormBiscuit
        artifact={artifact}
        threadId={thread.id}
        onDismiss={dismissOverlay}
        pagination={
          isQueue
            ? {
                index: activeOmniIndex,
                total: cards.length,
                onPrev: () => stepOmniArtifact(-1),
                onNext: advanceOrCommit,
              }
            : undefined
        }
        staging={
          isQueue
            ? {
                decision: decisions.get(artifact.id),
                decidedCount,
                isSubmitting: commitDecisions.isPending,
                onSkip: skipRemaining,
              }
            : undefined
        }
        followUp={omniFollowUp}
      />
    );
  };

  /**
   * What the composer area holds, decided by what the thread is waiting on: an
   * archived thread offers a way back in, an unresolved heavy artifact takes the
   * surface, and anything else is somewhere to type.
   */
  const renderComposer = () => {
    if (thread.status === "completed") {
      return (
        <Button
          variant="primary"
          size="lg"
          onClick={onUnarchive}
          data-statsig-name="thread-unarchive"
        >
          <Unarchive className="size-4" />
          Unarchive
        </Button>
      );
    }

    // `!biscuitArtifact` is redundant with `hasBiscuit` and kept for the
    // narrowing, which the card's own props need.
    if (!biscuitArtifact || !hasBiscuit || isWebView) return standaloneOmnibox;

    // When an unresolved heavy artifact is the most recent thread event, the
    // card takes over the omnibox area: it owns the outer chrome and embeds a
    // chrome-less Omnibox as the "Ask a follow up" peer row to the numbered
    // actions. The sparkle badge mirrors the square number badges so the
    // follow-up reads as a third action row, not as standalone omnibox chrome.
    //
    // Same outer width as the standalone Omnibox (`w-[688px]`) so the card state
    // and the omnibox state align edge-to-edge as the user moves between them.
    // The internal gutter lives inside `ArtifactBiscuit` as `p-3 @md:p-4` padding
    // around the content, mirroring the Omnibox's own internal padding, so the
    // visible chrome sits at the same outer rectangle either way.
    return <div className="w-[688px] max-w-full">{renderArtifactCard(biscuitArtifact)}</div>;
  };

  return (
    <div
      // pointer-events-none prevents the container from blocking interactions (esp. hover)
      // where there isn't actually rendered content
      // bottom-8 doesn't increase height, it offsets, so bottom-0 plus the shim's
      // own bottom padding gets the same positioning w/o blocking the last element
      // when scrolled to the bottom
      // mt-auto ensures the sticky container is always at the bottom of the list even when the list is short
      //
      // `isolate` pins the stacking context the ramp's negative z-index resolves
      // against. `sticky` already creates one, so this changes nothing today; it
      // is here so that losing `sticky` in a later layout change cannot silently
      // drop the ramp behind the thread instead of above it.
      className="sticky bottom-0 isolate pointer-events-none mt-auto"
    >
      <div className="relative pb-3 z-20 flex justify-center">
        <ScrollToBottomButton
          isAtBottom={isAtBottom}
          scrollContainerRef={scrollContainerRef}
          className="pointer-events-auto"
        />
      </div>
      {!readOnly && queuedUserItems.length > 0 && (
        <div className="flex justify-center pointer-events-auto">
          <div className="w-[688px] max-w-full">
            <QueuedMessageStack
              threadId={thread.id}
              queuedItems={queuedUserItems}
              onLocalCancel={removeSessionEvent}
            />
          </div>
        </div>
      )}
      {/* Inside the native app's WebView, the app owns the takeover screen, not this page. */}
      {!readOnly && !isWebView && (
        <BrowserTakeoverBar
          thread={thread}
          signInSlot={
            browserControlContainer && biscuitArtifact
              ? { artifactId: biscuitArtifact.id, container: browserControlContainer }
              : undefined
          }
        />
      )}
      {/* Outside the card above the composer, which opening the computer
          moves on: the desktop it opened stays up while the card goes. */}
      <ComputerDesktopOverlay threadId={thread.id} />
      {!readOnly && showExternalThreads && thread.status !== "completed" && (
        <ExternalChannelComposerNotice channel={thread.channel} />
      )}
      {/* The shim. The composer floats over a live scroller, so without it the
          conversation runs under the composer and stays readable around it,
          competing with the thing the user is typing into.

          Opaque from the composer's top edge down, which is why the paint sits
          here rather than on the footer: the rows above — the scroll-to-bottom
          button, the queued stack — stand over the thread, and a solid band
          behind them would read as a bar laid across it. Carrying the bottom
          inset here too keeps the floor covered, since text scrolls into that
          gap before it leaves the pane. 32px rather than 28px so the composer
          sits the same distance off the bottom here as on every other canvas; a
          4px difference between screens reads as drift rather than as design.

          Self-sizing: as tall as whatever the composer currently is — a card, a
          draft grown to several lines — so the shim never falls short of it the
          way a fixed band would. `PanelToolbar` opaques its sticky row against
          the same problem.

          A read-only thread keeps the inset and loses the paint. With no
          composer to protect, an opaque band would only veil the foot of a
          conversation the reader came to read.

          It also swallows the pointer, which is the other half of hiding
          something: a message painted out but still hoverable and clickable is
          a control the user cannot see themselves pressing. Only where it
          paints, though — the ramp above it opts back out, because a line at
          the top of the ramp is barely veiled at all, and a row that is fully
          legible but inert is worse than one you can still reach.

          Wheel scrolling is unaffected: the shim is not a scroller, so a wheel
          over it still reaches the thread underneath. */}
      <div
        className={cn("relative pb-8", !readOnly && "bg-background pointer-events-auto")}
        data-testid="composer-shim"
      >
        {!readOnly && (
          <>
            {/* The ramp: the approach to the shim. A shim that began at full
                opacity would cut lines at a hard edge; the ramp dissolves them
                into it, reaching full opacity exactly where the composer starts.

                24px is native's `FADE_RUN` (`ThreadEdgeFades.tsx`), so a line
                dissolves over the same distance on both surfaces. Native spends
                it differently — one gradient spanning the composer rather than a
                shim with a ramp above it — because it sizes from a measured
                `bottomInset`, where CSS can size a box to its own content.

                `-z-10` holds the ramp behind the footer's own rows. It is opaque
                where it meets them and the queued stack is translucent, so
                without it a queued message would be painted out entirely. The
                ramp veils the thread, never the composer's own chrome.

                It hands the pointer back, against the shim it belongs to: the
                gradient starts at nothing, so the top of this band shows the
                conversation at full strength. A hover bar or a link drawn there
                that quietly did nothing would read as a broken row rather than
                as a hidden one. */}
            <div
              aria-hidden
              data-testid="composer-ramp"
              className="pointer-events-none absolute inset-x-0 bottom-full -z-10 h-6 bg-gradient-to-t from-background to-transparent"
            />
            <div className="flex justify-center -mx-4 md:mx-0">{renderComposer()}</div>
          </>
        )}
        {sendError && (
          <div
            role="alert"
            className="mt-2 text-xs text-destructive text-center pointer-events-auto"
          >
            {sendError.message}
            {sendError.reason === UNITS_EXHAUSTED_REASON && (
              <>
                {" "}
                <Link
                  href={settingsTabPath("billing")}
                  className="underline underline-offset-2 font-medium"
                >
                  Manage your plan
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
